import React from "react";
import { AlertCircle } from "lucide-react";
import type { FormikProps } from "formik";
import { getIn } from "formik";

interface FormikErrorMessageProps<TValues = Record<string, unknown>> {
  formik: FormikProps<TValues>;
  name: string;
  className?: string;
  hasError?: boolean;
}

/**
 * Composant pour afficher les messages d'erreur Formik
 * Affiche l'erreur seulement si le champ a été touché et a une erreur
 */
export const FormikErrorMessage: React.FC<FormikErrorMessageProps> = ({
  formik,
  name,
  className = "",
  hasError,
}) => {
  // Récupérer l'erreur et l'état "touched" du champ
  const error = hasError
    ? "Cette valeur doit etre comprise entre 0 et 100"
    : getIn(formik.errors, name);

  const touched = getIn(formik.touched, name);
  const shouldShow = Boolean(touched || formik.submitCount > 0);

  // Ne pas afficher si pas d'erreur ou si le champ n'a pas été touché
  if (!error || !shouldShow) {
    return null;
  }

  return (
    <p className={`text-sm text-red-500 flex items-center gap-1 ${className}`}>
      <AlertCircle className="h-3 w-3" />
      {typeof error === "string" ? error : "Erreur de validation"}
    </p>
  );
};

/**
 * Hook pour obtenir les classes CSS d'erreur pour un champ
 */
export const useFormikFieldError = <TValues = Record<string, unknown>,>(
  formik: FormikProps<TValues>,
  name: string,
) => {
  const error = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);

  const hasError = !!(error && (touched || formik.submitCount > 0));
  const errorClass = hasError ? "border-red-500" : "";

  return {
    hasError,
    errorClass,
  };
};
